import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAccountStore } from '@/stores/account-store'
import { PostLoginNavigate } from '../components/post-login-navigate'
import { BETA } from '@/config/constants'
import { markLoginSubmitted, clearLoginSubmitted } from '@/services/analytics'
import { trackEvent } from '@/services/analytics/use-analytics'
import { EVENTS } from '@/services/analytics/events'
import { authErrorName } from '@/services/analytics/auth-error-name'
import { nextLoginFailureAttempt } from '@/services/analytics/login-marker'
import { loginFailureCategory } from '@/services/auth/login-error'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { NotificationToast } from '@/components/ui/notification-toast'
import { EdgeToTradeLogo } from '@/components/brand/edge-to-trade-logo'
import heroWorkspace from '@/assets/hero-workspace.webp'

export function LoginPage() {
  const navigate = useNavigate()
  const session = useAccountStore((s) => s.session)
  const isAuthenticating = useAccountStore((s) => s.isAuthenticating)
  const authError = useAccountStore((s) => s.authError)
  const loginMessage = useAccountStore((s) => s.loginMessage)
  const loginMessageTone = useAccountStore((s) => s.loginMessageTone)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  // Also covers a fresh sign-in: login() sets the session, which re-renders into this branch.
  if (session) return <PostLoginNavigate />

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    useAccountStore.getState().clearAuthError()
    // user_logged_in is captured by the analytics provider right after identify()
    // (login-marker.ts), so it always lands on the signed-in user.
    markLoginSubmitted()
    try {
      await useAccountStore.getState().login(email, password)
    } catch (err) {
      clearLoginSubmitted()
      if ((err as Error & { code?: string }).code === 'CONFIRM_SIGN_UP') {
        navigate('/verify-email', { replace: true, state: { email } })
        return
      }
      // Name and category only: never the email, the password or the message text.
      trackEvent(EVENTS.LOGIN_FAILED, {
        error_name: authErrorName(err),
        category: loginFailureCategory(err),
        attempt: nextLoginFailureAttempt(),
        method: 'password',
      })
      if ((err as Error & { code?: string }).code === 'RESET_PASSWORD') {
        navigate('/password-reset', { replace: true, state: { email } })
        return
      }
      // Other errors are already set in the store
    }
  }

  return (
    // overflow-hidden is what lets the artwork run past the right gutter without
    // adding a horizontal scrollbar.
    <div className="min-h-screen overflow-hidden bg-background">
      {/* One track for the logo and the content: 1280px of content plus the lg
          gutters. The header repeats the form column's width rules, so the logo's
          left edge sits on the card's left edge at every breakpoint. */}
      <div className="mx-auto flex min-h-screen w-full max-w-[1360px] flex-col px-6 py-8 lg:px-10">
        <header className="mx-auto w-full max-w-md shrink-0 lg:mx-0 lg:max-w-none" data-window-drag>
          <EdgeToTradeLogo className="h-7 w-auto text-foreground sm:h-9" />
        </header>

        <main className="flex w-full flex-1 items-center pt-12 lg:pt-0">
          <div className="grid w-full grid-cols-1 items-center gap-14 lg:grid-cols-5 lg:gap-10">
            <div className="mx-auto w-full max-w-md lg:col-span-2 lg:mx-0 lg:max-w-none">
              {/* Padding lives on the wrapper, and the two lines stay one inline
                  span: split into block lines the gradient restarts per line and
                  the purple falloff washes out. Nudged right to balance the card. */}
              <div className="pl-1.5">
                <h1 className="text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl lg:leading-[1.1]">
                  <span className="box-decoration-clone bg-gradient-to-r from-foreground via-foreground to-primary bg-clip-text text-transparent">
                    All in One Trading
                    <br />
                    Workspace.
                  </span>
                </h1>
              </div>

              <div className="mt-8 rounded-2xl border border-border p-5">
                {loginMessage && (
                  <div
                    role={loginMessageTone === 'warning' ? 'alert' : 'status'}
                    className={
                      loginMessageTone === 'warning'
                        ? 'mb-4 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm text-foreground'
                        : 'mb-4 rounded-md bg-positive/10 p-3 text-sm text-positive'
                    }
                  >
                    {loginMessage}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-foreground">
                      Email
                    </label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setEmail(e.target.value)
                      }
                      placeholder="you@example.com"
                      required
                      autoComplete="email"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="password" className="text-sm font-medium text-foreground">
                      Password
                    </label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setPassword(e.target.value)
                      }
                      placeholder="Password"
                      required
                      autoComplete="current-password"
                    />
                  </div>

                  {authError && (
                    <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                      {authError}
                    </div>
                  )}

                  <Button type="submit" className="w-full" disabled={isAuthenticating}>
                    {isAuthenticating ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        Signing in…
                      </span>
                    ) : (
                      'Sign in'
                    )}
                  </Button>
                </form>

                {/* Both gaps below the submit button are set here rather than inherited
                    from the card's rhythm. The wide one sets registration apart:
                    "Sign in"/"Sign up" read alike, so a new user has to spot it
                    without reading closely. */}
                <div className="space-y-10 pt-4 text-center">
                  <p className="text-sm text-muted-foreground">
                    <Link to="/password-reset" className="text-primary hover:underline">
                      Forgot password?
                    </Link>
                  </p>
                  {!BETA && (
                    <Button
                      asChild
                      variant="outline"
                      className="w-full border-primary/55 text-primary hover:bg-primary/10 hover:text-primary"
                    >
                      <Link to="/register">Create new account</Link>
                    </Button>
                  )}
                </div>
              </div>

              <p className="mt-5 px-2 text-center text-xs leading-relaxed text-muted-foreground">
                By continuing you agree to our{' '}
                <a
                  href="https://www.edgetotrade.com/legal/terms-of-service"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline"
                >
                  Terms and Conditions
                </a>{' '}
                and{' '}
                <a
                  href="https://www.edgetotrade.com/legal/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </div>

            {/* Decorative, and the heaviest asset on the page. `loading="lazy"`
                keeps phones from fetching it at all: the column is display:none
                below lg, so the image never intersects the viewport. */}
            <div className="hidden lg:col-span-3 lg:block">
              <img
                src={heroWorkspace}
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
                draggable={false}
                className="w-[125%] max-w-none select-none"
              />
            </div>
          </div>
        </main>
      </div>
      <NotificationToast />
    </div>
  )
}
